import { cloneElement, lazy, Suspense, useEffect } from 'react';

import { RETRY_LAZY_REFRESHED } from 'Lib/serviceWorkerHelper';

type LoaderType<T> = () => Promise<{ default: React.ComponentType<T> }>;

type AsyncComponentExtraProps = {
  onDidMount?: VoidFunction;
};

type AsyncLoaderOptions<TPropsRaw> = {
  loader: LoaderType<TPropsRaw>;
  name?: string;
  fallback?: Parameters<typeof cloneElement>[0] | null;
};

/**
 * Dynamically import a component with a retry functionality to avoid ChunkLoadError after a release
 *
 * @param loader dynamic import loader
 * @returns loaded component
 */
const lazyWithRefreshed = <T,>(loader: LoaderType<T>) =>
  lazy(async () =>
    loader().catch((error) => {
      const hasRefreshed = window.sessionStorage.getItem(RETRY_LAZY_REFRESHED) === 'true';
      if (!hasRefreshed) {
        window.sessionStorage.setItem(RETRY_LAZY_REFRESHED, 'true');
        window.location.reload();
        // Note that return is necessary to prevent the current execution proceed and throw the error before reloading the page
        // Here we return a placeholder component to ensure that no error will throw during rendering in the AsyncLoader
        return { default: () => <></> };
      }
      // If the user still encountered ChunkLoadError after refreshed,
      // then it's something else causing the issue (ex. network issues, firewalls),
      // proceeding with the default error behavior (crash & report).
      throw error;
    })
  );

function AsyncLoader<
  TPropsRaw,
  // if the inferred loader props are unknown (likely from the module not being written
  // in TypeScript), use `any` for the returned component props
  TProps = unknown extends TPropsRaw ? any : TPropsRaw
>({
  loader,
  name,
  fallback = null,
}: AsyncLoaderOptions<TPropsRaw>): React.ComponentType<TProps & AsyncComponentExtraProps> {
  const Component = lazyWithRefreshed(loader);

  const AsyncComponent = (props: TProps & AsyncComponentExtraProps) => {
    const { onDidMount, ...restProps } = props;

    useEffect(() => {
      if (onDidMount) {
        onDidMount();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // only execute on initial mount

    return (
      <Suspense fallback={fallback ? cloneElement(fallback, restProps) : null}>
        {/* @ts-expect-error: restProps will align with the prop type of Component */}
        <Component {...restProps} />
      </Suspense>
    );
  };

  AsyncComponent.preload = loader;
  AsyncComponent.displayName = name;

  return AsyncComponent;
}

export default AsyncLoader;
